import React, { useContext, useRef } from 'react';
import { dataContext } from '../../context/UserContext';
import './ResumePreview.css';

function ResumePreview(){
  const { resumeImproved, resumeGenerated, resumeAnswers } = useContext(dataContext);
  const printRef = useRef(null);

  if(!resumeGenerated || !resumeImproved) return null;

  const name = resumeAnswers.name || '';
  const contact = resumeAnswers.contact || '';

  function handleDownload(){
    // Hidden iframe print avoids reloading the React app
    if(!printRef.current) return;
    const iframe = document.createElement('iframe');
    iframe.style.position='fixed'; iframe.style.right='0'; iframe.style.bottom='0'; iframe.style.width='0'; iframe.style.height='0'; iframe.style.border='0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    const css = `body{margin:0;font-family:Inter,system-ui,Arial,sans-serif;} .resume-page{background:#fff;color:#222;padding:40px 48px;font-size:13px;line-height:1.3;} .resume-page h1{margin:0 0 4px;font-size:34px;} .resume-page h2{margin:28px 0 8px;font-size:18px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #ddd;padding-bottom:4px;} .resume-page ul{margin:4px 0 12px 18px;padding:0;} .resume-page li{margin:0 0 4px;} .resume-footer{margin-top:24px;text-align:center;font-size:11px;color:#666;} @media print{ .resume-page{box-shadow:none;margin:0;width:100%;} }`;
    doc.write('<!DOCTYPE html><html><head><title>Resume</title><style>'+css+'</style></head><body>'+printRef.current.outerHTML+'</body></html>');
    doc.close();
    iframe.onload = () => {
      setTimeout(()=>{
        try{ iframe.contentWindow.focus(); iframe.contentWindow.print(); }catch(e){}
        setTimeout(()=>{ if(iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 400);
      }, 50);
    };
  }

  return (
    <div className="resume-wrapper">
      <div className="resume-toolbar no-print">
        <button onClick={handleDownload}>Download PDF</button>
      </div>
      <div className="resume-page" ref={printRef}>
        <header className="resume-header">
          <h1>{name}</h1>
          {contact && <p className="contact-line">{contact}</p>}
        </header>
        <section>
          <h2>Professional Summary</h2>
          <p>{resumeImproved.professionalSummary}</p>
        </section>
        {resumeImproved.careerObjectiveRefined && (
          <section>
            <h2>Career Objective</h2>
            <p>{resumeImproved.careerObjectiveRefined}</p>
          </section>
        )}
        {Array.isArray(resumeImproved.workExperienceBullets) && resumeImproved.workExperienceBullets.length>0 && (
          <section>
            <h2>Experience</h2>
            {resumeImproved.workExperienceBullets.map((exp, idx)=>{
              // Support new structured object form { company, title, dates, description: [] }
              if(exp && typeof exp === 'object' && !Array.isArray(exp)){
                const bullets = Array.isArray(exp.description) ? exp.description : [];
                return (
                  <div key={idx} className="exp-block">
                    <div className="exp-heading">
                      <strong>{exp.title || ''}</strong>{exp.company ? ` | ${exp.company}` : ''}{exp.dates ? ` (${exp.dates})` : ''}
                    </div>
                    {bullets.length > 0 && (
                      <ul>
                        {bullets.map((b,i)=>(<li key={i}>{b}</li>))}
                      </ul>
                    )}
                  </div>
                );
              }
              // If it's an array treat as list of bullet strings
              if(Array.isArray(exp)){
                return (
                  <div key={idx} className="exp-block">
                    <ul>{exp.map((b,i)=>(<li key={i}>{b}</li>))}</ul>
                  </div>
                );
              }
              // Fallback simple string
              return (
                <div key={idx} className="exp-block">
                  <ul><li>{exp}</li></ul>
                </div>
              );
            })}
          </section>
        )}
        {resumeImproved.skillsGrouped && Object.keys(resumeImproved.skillsGrouped).length>0 && (
          <section className="skills-grid">
            <h2>Skills</h2>
            <div className="skill-columns">
              {Object.entries(resumeImproved.skillsGrouped).map(([cat, arr])=> (
                <div key={cat} className="skill-col">
                  <h3>{cat}</h3>
                  <p>{arr.join(', ')}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        {Array.isArray(resumeImproved.projectsRefined) && resumeImproved.projectsRefined.length>0 && (
          <section>
            <h2>Projects</h2>
            <ul>
              {resumeImproved.projectsRefined.map((p,i)=>{
                if(p && typeof p === 'object' && !Array.isArray(p)){
                  return (
                    <li key={i}>
                      <strong>{p.name || 'Project'}:</strong> {p.description || ''}
                    </li>
                  );
                }
                return <li key={i}>{p}</li>;
              })}
            </ul>
          </section>
        )}
        {Array.isArray(resumeImproved.achievementsRefined) && resumeImproved.achievementsRefined.length>0 && (
          <section>
            <h2>Achievements & Awards</h2>
            <ul>
              {resumeImproved.achievementsRefined.map((a,i)=>(<li key={i}>{a}</li>))}
            </ul>
          </section>
        )}
        <footer className="resume-footer">
          <span>Generated with assistance from VedaTron-AI</span>
        </footer>
      </div>
    </div>
  );
}

export default ResumePreview;
